import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { inRange } from 'lodash/fp';
import { Fragment } from 'react';
import { Field, FormSection } from 'redux-form';
import styled from 'styled-components';

import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import {
  FieldWrapper,
  SubRowLabel,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import InputField from 'dashboard/components/FormControls/InputField';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { ExpirationIntervals } from 'dashboard/models/inquiry-template-version';
import { Paragraph } from 'design-system/components/Text';
import { withMessage } from 'lib/utils/formValidation';

export const ONE_MINUTE_SECONDS = 60;
export const SIX_MONTHS_MINUTES = 262800;

type ExpirationInterval = keyof ExpirationIntervals;
export const ExpirationIntervalNames: ExpirationInterval[] = [
  'expiration-after-create-interval-seconds',
  'expiration-after-start-interval-seconds',
  'expiration-after-resume-interval-seconds',
  'one-time-link-expiration-seconds',
];

export const EXPIRATION_INTERVAL_LABELS: Record<ExpirationInterval, string> = {
  ['expiration-after-create-interval-seconds']: 'From inquiry creation',
  ['expiration-after-start-interval-seconds']: 'From inquiry start',
  ['expiration-after-resume-interval-seconds']: 'From inquiry resume',
  ['one-time-link-expiration-seconds']: 'Device handoff link',
};

export const EXPIRATION_INTERVAL_PLACEHOLDERS: Record<ExpirationInterval, string> = {
  ['expiration-after-create-interval-seconds']: '1440',
  ['expiration-after-start-interval-seconds']: 'None',
  ['expiration-after-resume-interval-seconds']: '1440',
  ['one-time-link-expiration-seconds']: '60',
};

export const EXPIRATION_INTERVAL_HINTS: Record<ExpirationInterval, string> = {
  ['expiration-after-create-interval-seconds']: 'By default Inquiries expire 1 day after creation.',
  ['expiration-after-start-interval-seconds']:
    'If not set, the Inquiry will expire at the threshold configured for creation (above).\n\nIf set, the Inquiry will expire at whichever of the two thresholds comes first',
  ['expiration-after-resume-interval-seconds']:
    'By default Inquiries expire 1 day after a session is resumed.',
  ['one-time-link-expiration-seconds']: 'By default, device handoff links expire in 1 hour.',
};

export const INTERVAL_RANGE_ERROR = 'Must be between 0 and 6 months';

const ExpirationRow = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const StyledFieldWrapper = styled(FieldWrapper)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledInputField = styled(InputField).attrs(() => ({ size: 'md' }))`
  width: 120px;
`;

const Description = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${(props) => props.theme.color.text.primary};
`;

const validation = [
  withMessage(INTERVAL_RANGE_ERROR, (value: number | undefined) =>
    value == null || value === 0
      ? true
      : inRange(0, SIX_MONTHS_MINUTES + 1, Math.floor(value / ONE_MINUTE_SECONDS)),
  ),
];

export const parseIntervalValue = (value: string) =>
  value !== '' ? parseInt(value) * ONE_MINUTE_SECONDS : null;

export const formatIntervalValue = (value: number | undefined) =>
  value != null && value >= 0 ? Math.floor(value / ONE_MINUTE_SECONDS) : null;

const FormSectionExpirationsConfig = () => {
  const editableForBasicTier = useIsEditableWithOptions({
    accessTier: EditorAccessTier.InquiriesBasic,
  });
  return (
    <FormSection name='expiration-intervals' component={Fragment}>
      {ExpirationIntervalNames.map((interval, idx) => (
        <ExpirationRow key={`${interval}-${idx}`}>
          <Description>
            {EXPIRATION_INTERVAL_LABELS[interval]}
            <HelpIcon icon={<CircleHelpIcon />}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{EXPIRATION_INTERVAL_HINTS[interval]}</div>
            </HelpIcon>
          </Description>
          <StyledFieldWrapper>
            <Field
              id={interval}
              key={interval}
              name={interval}
              max={SIX_MONTHS_MINUTES}
              min={0}
              type='number'
              placeholder={EXPIRATION_INTERVAL_PLACEHOLDERS[interval]}
              data-test={`form-expirations-${interval}__field-input`}
              component={StyledInputField}
              label={<SubRowLabel>Minutes</SubRowLabel>}
              disabled={!editableForBasicTier}
              validate={validation}
              parse={parseIntervalValue}
              format={formatIntervalValue}
            />
          </StyledFieldWrapper>
        </ExpirationRow>
      ))}
    </FormSection>
  );
};

export default FormSectionExpirationsConfig;
